import {
  Building2,
  CircleUser,
  ClipboardCheck,
  FileSignature,
  FileText,
  Gauge,
  House,
  KeyRound,
  LifeBuoy,
  Megaphone,
  MessageSquare,
  Receipt,
  Signpost,
  Sparkles,
  TrendingUp,
  Wallet,
} from 'lucide-react';

/**
 * Portal navigation (docs/frontend/portal.md §1), role-based.
 * Visibility: any listed role OR any listed permission (see @blimo/shared canAccess).
 * Finance entries follow the permission `finance.view` (the finance API answers 403 without it),
 * `Mieter` `tenancies.view`, `Vermietung` `lettings.view`, `Vollmachten` `ownerships.view`, `Zählerablesung` role meter-reader or `meter-readings.manage`,
 * `Dokumente` `documents.view`, `Blimo AI` `assistant.use` **and** `permitted` of `GET /portal/assistant/status` (`requires: 'assistant'`, checked in
 * the shell); the other entries are role-based. UX only – the API enforces access.
 *
 * `group`: section of the sidebar (`NAV_GROUPS`, rendered in this order; items keep their order within a group).
 *
 * @typedef {{ id: string, label: string, shortLabel?: string, to: string, icon: any, group: NavGroupId, access?: import('@blimo/shared').AccessRule, requires?: 'assistant', features?: string }} PortalNavItem
 * @typedef {'overview'|'assets'|'communication'|'service'} NavGroupId
 */

/** @type {PortalNavItem[]} */
export const portalNav = [
  { id: 'overview', group: 'overview', label: 'Übersicht', to: '/', icon: House, features: 'F-1004, F-601' },
  {
    id: 'assistant',
    group: 'overview',
    label: 'Blimo AI',
    to: '/assistent',
    icon: Sparkles,
    access: { permissions: ['assistant.use'] },
    requires: 'assistant',
    features: 'Chat-Assistent über die eigenen Portaldaten',
  },
  {
    id: 'properties',
    group: 'assets',
    label: 'Meine Immobilien',
    shortLabel: 'Immobilien',
    to: '/immobilien',
    icon: Building2,
    access: { roles: ['owner', 'investor'] },
    features: 'F-123–F-126',
  },
  {
    id: 'development',
    group: 'assets',
    label: 'Entwicklung',
    to: '/entwicklung',
    icon: TrendingUp,
    access: { permissions: ['finance.view'] },
    features: 'F-602, F-603',
  },
  {
    id: 'open-items',
    group: 'assets',
    label: 'Offene Posten',
    to: '/offene-posten',
    icon: Wallet,
    access: { permissions: ['finance.view'] },
    features: 'F-606, F-607',
  },
  { id: 'costs', group: 'assets', label: 'Kosten', to: '/kosten', icon: Receipt, access: { permissions: ['finance.view'] }, features: 'F-604' },
  {
    id: 'tenants',
    group: 'assets',
    label: 'Mieter',
    to: '/mieter',
    icon: KeyRound,
    access: { permissions: ['tenancies.view'] },
    features: 'F-702–F-704',
  },
  {
    id: 'lettings',
    group: 'assets',
    label: 'Vermietung',
    to: '/vermietung',
    icon: Signpost,
    access: { permissions: ['lettings.view'] },
    features: 'F-608, F-1002',
  },
  {
    id: 'documents',
    group: 'communication',
    label: 'Dokumente',
    to: '/dokumente',
    icon: FileText,
    access: { permissions: ['documents.view'] },
    features: 'F-405, F-807',
  },
  { id: 'announcements', group: 'communication', label: 'Mitteilungen', to: '/mitteilungen', icon: Megaphone, access: { roles: ['owner'] }, features: 'F-503' },
  { id: 'messages', group: 'communication', label: 'Nachrichten', to: '/nachrichten', icon: MessageSquare, access: { roles: ['owner'] }, features: 'F-504' },
  {
    id: 'document-checks',
    group: 'service',
    label: 'Belegprüfung',
    to: '/belegpruefung',
    icon: ClipboardCheck,
    access: { roles: ['auditor', 'owner'] },
    features: 'F-804, F-808, F-809',
  },
  {
    id: 'meter-readings',
    group: 'service',
    label: 'Zählerablesung',
    shortLabel: 'Zähler',
    to: '/zaehlerablesung',
    icon: Gauge,
    access: { roles: ['meter-reader'], permissions: ['meter-readings.manage'] },
    features: 'F-910',
  },
  {
    id: 'proxies',
    group: 'service',
    label: 'Vollmachten',
    to: '/vollmachten',
    icon: FileSignature,
    access: { permissions: ['ownerships.view'] },
    features: 'F-1001',
  },
  { id: 'service', group: 'service', label: 'Service', to: '/service', icon: LifeBuoy, features: 'F-407, F-1003 (Formulare, Links)' },
  {
    id: 'profile',
    group: 'service',
    label: 'Mein Profil',
    shortLabel: 'Profil',
    to: '/profil',
    icon: CircleUser,
    features: 'F-008, F-016, 2FA, Benachrichtigungen',
  },
];

/** Sidebar sections (order = display order). */
export const NAV_GROUPS = /** @type {const} */ ([
  { id: 'overview', label: 'Übersicht' },
  { id: 'assets', label: 'Immobilien & Finanzen' },
  { id: 'communication', label: 'Kommunikation' },
  { id: 'service', label: 'Service & Konto' },
]);

/**
 * Visible items grouped into the sidebar sections; empty sections are dropped.
 * @param {PortalNavItem[]} items  already filtered items
 * @returns {Array<{ id: string, label: string, items: PortalNavItem[] }>}
 */
export function groupNavItems(items) {
  return NAV_GROUPS.map((group) => ({ ...group, items: items.filter((item) => item.group === group.id) })).filter((group) => group.items.length > 0);
}
